import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Avatar,
    Stack,
    Chip,
    Pagination,
    alpha,
    useTheme,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon, Clear as ClearIcon } from '@mui/icons-material';
import {
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon,
    Add as AddIcon,
    ChangeCircle as ChangeCircleIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { decodeTime } from 'ulid';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { useAuditSearchQuery } from '@features/redux/apis/audit';

import type { Dayjs } from 'dayjs';
import type { AuditLogRecord, AuditSearchParams } from '@features/redux/apis/audit';

/* ------------------------------------------------------------------ */
/* Types and Constants                                                */
/* ------------------------------------------------------------------ */

interface SearchFilters {
    operationType: string;
    entityType: string;
    entityPk: string;
    fieldsChanged: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    searchTerm: string;
}

interface AdminAuditEntry {
    email: string;
    op: string;
    ts?: string;
    userId: string;
    entityPk: string;
    delta: Record<string, { before?: unknown; after?: unknown }>;
}

const OPERATION_TYPES = [
    { value: '', label: 'All Operations' },
    { value: 'CREATE', label: 'Create' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
];

const ENTITY_TYPES = [
    { value: '', label: 'All Entities' },
    { value: 'Station', label: 'Station' },
    { value: 'Location', label: 'Location' },
    { value: 'Company', label: 'Company' },
    { value: 'Reseller', label: 'Reseller' },
];

const PAGE_SIZES = [25, 50, 100];

/* ------------------------------------------------------------------ */
/* Helper Functions                                                   */
/* ------------------------------------------------------------------ */

const getOperationColor = (theme: any, op: string): string => {
    switch (op) {
        case 'CREATE':
            return theme.palette.success.main;
        case 'UPDATE':
            return theme.palette.info.main;
        case 'DELETE':
            return theme.palette.error.main;
        default:
            return theme.palette.text.secondary;
    }
};

const getOperationIcon = (op: string): React.ReactElement => {
    switch (op) {
        case 'CREATE':
            return <AddIcon fontSize='small' />;
        case 'UPDATE':
            return <ChangeCircleIcon fontSize='small' />;
        case 'DELETE':
            return <DeleteIcon fontSize='small' />;
        default:
            return <ChangeCircleIcon fontSize='small' />;
    }
};

const getInitials = (email?: string): string => email?.[0]?.toUpperCase() ?? '?';

const formatValue = (value: unknown): string =>
    value === null || value === undefined ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);

const getChangedFieldsCount = (delta: Record<string, { before?: unknown; after?: unknown }>): number =>
    Object.entries(delta).filter(([, deltaData]) => {
        return deltaData.before !== deltaData.after;
    }).length;

// Transform API data to match AdminAuditLogTable expected format
const transformAuditData = (items: AuditLogRecord[]): AdminAuditEntry[] => {
    return items.map((item) => ({
        email: item.email,
        op: item.op,
        ts: item.ts,
        userId: item.userId,
        entityPk: item.entityPk,
        // Transform the delta structure to match expected format
        delta: item.delta
            ? Object.entries(item.delta).reduce((acc, [key, value]) => {
                  acc[key] = {
                      before: item.before?.[key],
                      after: item.after?.[key] || value,
                  };
                  return acc;
              }, {} as Record<string, { before: unknown; after: unknown }>)
            : {},
    }));
};

/* ------------------------------------------------------------------ */
/* AdminAuditLogTable Component with Entity Info                     */
/* ------------------------------------------------------------------ */

interface ExpandableRowProps {
    audit: AdminAuditEntry;
    isExpanded: boolean;
    onToggleExpanded: () => void;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ audit, isExpanded, onToggleExpanded }) => {
    const theme = useTheme();
    const timestamp = audit.ts ? dayjs(decodeTime(audit.ts.split('ts#')[1])) : null;
    const changedFieldsCount = getChangedFieldsCount(audit.delta);

    // Extract entity type and ID from entityPk
    const entityType = audit.entityPk.split('#')[0] || 'Unknown';
    const entityId = audit.entityPk.split('#')[1] || audit.entityPk;

    const OperationChip = ({ operation }: { operation: string }) => (
        <Chip
            size='small'
            icon={getOperationIcon(operation)}
            label={operation.charAt(0) + operation.slice(1).toLowerCase()}
            variant='outlined'
            sx={{
                borderColor: getOperationColor(theme, operation),
                color: getOperationColor(theme, operation),
                '& .MuiChip-icon': { color: getOperationColor(theme, operation) },
                fontWeight: 600,
                letterSpacing: 0.25,
            }}
        />
    );

    return (
        <>
            <TableRow
                hover
                sx={{
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                }}
                onClick={onToggleExpanded}>
                <TableCell>
                    <IconButton size='small'>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </TableCell>

                <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>{getInitials(audit.email)}</Avatar>
                        <Box>
                            <Typography variant='body2' fontWeight={500}>
                                {audit.email}
                            </Typography>
                        </Box>
                    </Stack>
                </TableCell>

                <TableCell>
                    <OperationChip operation={audit.op} />
                </TableCell>

                <TableCell>
                    <Typography variant='body2'>{timestamp ? timestamp.format('MMM D, YYYY') : '—'}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        {timestamp ? timestamp.format('h:mm A') : ''}
                    </Typography>
                </TableCell>

                {/* Entity Column - This is the new addition for AdminAuditLog */}
                <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                        {entityType}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                        {entityId}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                        {changedFieldsCount} field{changedFieldsCount !== 1 ? 's' : ''} changed
                    </Typography>
                    {changedFieldsCount > 0 && (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                            {Object.keys(audit.delta).join(', ')}
                        </Typography>
                    )}
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                        <Box sx={{ margin: 1, p: 2 }}>
                            <Typography variant='h6' gutterBottom component='div' sx={{ mb: 2 }}>
                                Changed Fields
                            </Typography>

                            {Object.entries(audit.delta).length === 0 ? (
                                <Typography variant='body2' color='text.secondary'>
                                    No field changes recorded
                                </Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {Object.entries(audit.delta).map(([fieldName, { before, after }]) => (
                                        <Grid item xs={12} key={fieldName}>
                                            <Paper
                                                variant='outlined'
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                                }}>
                                                <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                                                    {fieldName}
                                                </Typography>

                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            Before:
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                                p: 1,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                wordBreak: 'break-all',
                                                            }}>
                                                            {formatValue(before)}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            After:
                                                        </Typography>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                backgroundColor: alpha(theme.palette.success.main, 0.1),
                                                                p: 1,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                wordBreak: 'break-all',
                                                            }}>
                                                            {formatValue(after)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

interface AdminAuditLogTableProps {
    audits: AdminAuditEntry[];
    pageSize?: number;
}

const AdminAuditLogTable: React.FC<AdminAuditLogTableProps> = ({ audits, pageSize = 10 }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [operationFilter, setOperationFilter] = useState<string>('ALL');
    const [sortField, setSortField] = useState<'timestamp' | 'email' | 'operation'>('timestamp');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [currentPage, setCurrentPage] = useState<number>(1);

    const handleSort = (field: 'timestamp' | 'email' | 'operation') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const toggleRowExpanded = (index: number) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    const filteredAndSortedAudits = useMemo(() => {
        const filtered = audits.filter((audit) => {
            const matchesSearch = searchTerm
                ? audit.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  audit.entityPk.toLowerCase().includes(searchTerm.toLowerCase())
                : true;
            const matchesOperation = operationFilter === 'ALL' || audit.op === operationFilter;
            return matchesSearch && matchesOperation;
        });

        // Sort
        filtered.sort((a, b) => {
            let aValue: any, bValue: any;

            switch (sortField) {
                case 'timestamp':
                    aValue = a.ts ? decodeTime(a.ts.split('ts#')[1]) : 0;
                    bValue = b.ts ? decodeTime(b.ts.split('ts#')[1]) : 0;
                    break;
                case 'email':
                    aValue = a.email.toLowerCase();
                    bValue = b.email.toLowerCase();
                    break;
                case 'operation':
                    aValue = a.op;
                    bValue = b.op;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [audits, searchTerm, operationFilter, sortField, sortDirection]);

    const totalPages = Math.ceil(filteredAndSortedAudits.length / pageSize);
    const paginatedAudits = filteredAndSortedAudits.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <Paper elevation={1}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width={48}></TableCell>
                            <TableCell
                                sortDirection={sortField === 'email' ? sortDirection : false}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleSort('email')}>
                                User
                            </TableCell>
                            <TableCell
                                sortDirection={sortField === 'operation' ? sortDirection : false}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleSort('operation')}>
                                Operation
                            </TableCell>
                            <TableCell
                                sortDirection={sortField === 'timestamp' ? sortDirection : false}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleSort('timestamp')}>
                                Timestamp
                            </TableCell>
                            <TableCell>Entity</TableCell>
                            <TableCell>Changes</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedAudits.map((audit, index) => {
                            const globalIndex = (currentPage - 1) * pageSize + index;
                            return (
                                <ExpandableRow
                                    key={globalIndex}
                                    audit={audit}
                                    isExpanded={expandedRows.has(globalIndex)}
                                    onToggleExpanded={() => toggleRowExpanded(globalIndex)}
                                />
                            );
                        })}

                        {paginatedAudits.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color='text.secondary'>
                                        No audit entries found matching your search criteria.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color='primary'
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Paper>
    );
};

/* ------------------------------------------------------------------ */
/* Main AdminAuditLog Component                                      */
/* ------------------------------------------------------------------ */

export const AdminAuditLog: React.FC = () => {
    const [filters, setFilters] = useState<SearchFilters>({
        operationType: '',
        entityType: '',
        entityPk: '',
        fieldsChanged: '',
        fromDate: null,
        toDate: null,
        searchTerm: '',
    });

    const [pageSize, setPageSize] = useState<number>(25);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string>('');

    // Build search parameters
    const searchParams = useMemo<Omit<AuditSearchParams, 'token'>>(() => {
        const params: Omit<AuditSearchParams, 'token'> = {
            limit: pageSize,
        };

        if (filters.operationType) params.operationType = filters.operationType;
        if (filters.entityType) params.entityType = filters.entityType;
        if (filters.entityPk) params.entityPk = filters.entityPk;
        if (filters.fieldsChanged) params.fieldsChanged = filters.fieldsChanged;
        if (filters.fromDate) params.from = filters.fromDate.toISOString();
        if (filters.toDate) params.to = filters.toDate.toISOString();
        if (lastEvaluatedKey) params.lastEvaluatedKey = lastEvaluatedKey;

        return params;
    }, [filters, pageSize, lastEvaluatedKey]);

    // API call
    const { data, error, isFetching, refetch } = useAuditSearchQuery(searchParams);

    const handleFilterChange = useCallback((field: keyof SearchFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setLastEvaluatedKey('');
        setCurrentPage(1);
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters({
            operationType: '',
            entityType: '',
            entityPk: '',
            fieldsChanged: '',
            fromDate: null,
            toDate: null,
            searchTerm: '',
        });
        setLastEvaluatedKey('');
        setCurrentPage(1);
    }, []);

    const handlePageChange = useCallback(
        (event: React.ChangeEvent<unknown>, page: number) => {
            if (page > currentPage && data?.pagination.lastEvaluatedKey) {
                // Going to next page - use the lastEvaluatedKey
                const encodedKey = btoa(JSON.stringify(data.pagination.lastEvaluatedKey));
                setLastEvaluatedKey(encodedKey);
            } else if (page < currentPage) {
                // Going back - reset to first page (simplified approach)
                setLastEvaluatedKey('');
                setCurrentPage(1);
                return;
            }
            setCurrentPage(page);
        },
        [currentPage, data?.pagination.lastEvaluatedKey]
    );

    const hasActiveFilters = useMemo(() => {
        return Object.values(filters).some((value) => value !== '' && value !== null);
    }, [filters]);

    // Transform and filter data locally by search term if provided
    const transformedData = useMemo(() => {
        if (!data?.items) return [];

        let transformed = transformAuditData(data.items);

        // Apply local search filter
        if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            transformed = transformed.filter(
                (item) =>
                    item.email.toLowerCase().includes(searchLower) ||
                    item.entityPk.toLowerCase().includes(searchLower) ||
                    item.userId.toLowerCase().includes(searchLower)
            );
        }

        return transformed;
    }, [data?.items, filters.searchTerm]);

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <Box sx={{ mt: '180px', p: 3 }}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <FilterIcon />
                        <Typography variant='h6' fontWeight={600}>
                            Search & Filters
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                size='small'
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                variant='outlined'>
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label='Search'
                                placeholder='Search by email, entity, etc...'
                                value={filters.searchTerm}
                                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Operation Type</InputLabel>
                                <Select
                                    value={filters.operationType}
                                    label='Operation Type'
                                    onChange={(e) => handleFilterChange('operationType', e.target.value)}>
                                    {OPERATION_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Entity Type</InputLabel>
                                <Select
                                    value={filters.entityType}
                                    label='Entity Type'
                                    onChange={(e) => handleFilterChange('entityType', e.target.value)}>
                                    {ENTITY_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label='Entity ID'
                                placeholder='e.g., Station#4321'
                                value={filters.entityPk}
                                onChange={(e) => handleFilterChange('entityPk', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label='Fields Changed'
                                placeholder='e.g., name,status,email'
                                value={filters.fieldsChanged}
                                onChange={(e) => handleFilterChange('fieldsChanged', e.target.value)}
                                helperText='Comma-separated field names'
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label='From Date'
                                    value={filters.fromDate}
                                    onChange={(date) => handleFilterChange('fromDate', date)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label='To Date'
                                    value={filters.toDate}
                                    onChange={(date) => handleFilterChange('toDate', date)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Error State */}
                {error && (
                    <Alert
                        severity='error'
                        action={
                            <Button color='inherit' size='small' onClick={() => refetch()}>
                                Retry
                            </Button>
                        }
                        sx={{ mb: 3 }}>
                        Failed to load audit data. Please try again.
                    </Alert>
                )}

                {/* Loading State */}
                {isFetching && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Summary and Audit Table */}
                {!isFetching && (
                    <Box>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant='body2' color='text.secondary'>
                                {`${transformedData.length} records found`}
                            </Typography>
                            <FormControl size='small' sx={{ minWidth: 120 }}>
                                <InputLabel>Page Size</InputLabel>
                                <Select
                                    value={pageSize}
                                    label='Page Size'
                                    onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {PAGE_SIZES.map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Use custom AdminAuditLogTable component with entity information */}
                        <AdminAuditLogTable audits={transformedData} pageSize={pageSize} />

                        {/* MUI Pagination with next/back arrows only - only show if there are multiple pages */}
                        {(currentPage > 1 || data?.pagination.hasMore) && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <Pagination
                                    page={currentPage}
                                    count={data?.pagination.hasMore ? currentPage + 1 : currentPage}
                                    onChange={handlePageChange}
                                    color='primary'
                                    hidePrevButton={currentPage === 1}
                                    hideNextButton={!data?.pagination.hasMore}
                                    disabled={isFetching}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            '&.Mui-disabled': {
                                                opacity: 0.5,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </BasePage>
    );
};
